import React, {
  Dispatch,
  SetStateAction,
  KeyboardEvent,
  useRef,
  useEffect,
} from 'react';
import { IconButton, InputAdornment } from '@material-ui/core';
import {
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  Fullscreen as FullscreenIcon,
  Send as SendIcon,
} from '@material-ui/icons';

import { DefaultMessage } from 'modules/mobiCopilot/utils/interfaces';

import { InitialMessage } from './InitialMessage';
import { Message } from './Message';

import { groupMessagesByDay, getUserName } from './functions';

import * as S from './styles';
import { MobiCopilotLogo } from 'modules/mobiCopilot/utils/constants';

interface Props {
  currentMessage: string;
  currentUserName: string;
  isFullScreen: boolean;
  isLoading?: boolean;
  lastMessages?: DefaultMessage[];
  messageSuggestions?: string[];
  onClear: () => void;
  onClose: () => void;
  onFullScreen: () => void;
  onSubmit: (message: string) => void;
  pendingSocketResponse?: boolean;
  setCurrentMessage: Dispatch<SetStateAction<string>>;
}

export function DefaultContent({
  currentMessage,
  currentUserName,
  isFullScreen,
  isLoading = false,
  lastMessages = [],
  messageSuggestions = [],
  onClear,
  onClose,
  onFullScreen,
  onSubmit,
  pendingSocketResponse = false,
  setCurrentMessage,
}: Props) {
  const contentRef = useRef<HTMLDivElement>(null);

  const currentMessages = groupMessagesByDay(lastMessages);

  const generalLoading = isLoading || pendingSocketResponse;

  const hasMessages = currentMessages?.length > 0;

  const { initials: userInitials, name: userName } = getUserName(
    currentUserName
  );

  const handleScrollToBottom = () => {
    if (!contentRef?.current) return;

    const { current } = contentRef;

    return current.scroll({
      top: current.scrollHeight - current.clientHeight,
      behavior: 'auto',
    });
  };

  const handleOnSubmit = (value: string) => {
    onSubmit(value);
    handleScrollToBottom();
  };

  const handleKeyDown = ({ key, target }: KeyboardEvent<HTMLInputElement>) => {
    if (key === 'Enter' || key === 'NumpadEnter') {
      const typed = target as HTMLInputElement;
      handleOnSubmit(typed.value);
    }
  };

  useEffect(() => {
    if (!hasMessages) return;

    return handleScrollToBottom();
  }, [currentMessages]);

  const onLastGroup = currentIndex =>
    currentMessages?.length - 1 === currentIndex;

  return (
    <S.Container data-is-fullscreen={isFullScreen}>
      <S.Header>
        <S.Title>
          <MobiCopilotLogo />
        </S.Title>
        <S.Actions>
          <IconButton
            onClick={onClear}
            disabled={!hasMessages || generalLoading}
          >
            <DeleteOutlineIcon />
          </IconButton>
          <IconButton onClick={onFullScreen}>
            <FullscreenIcon />
          </IconButton>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </S.Actions>
      </S.Header>
      <S.Content ref={contentRef} className="content">
        {!hasMessages && (
          <InitialMessage
            isFullScreen={isFullScreen}
            isLoading={generalLoading}
            messageSuggestions={messageSuggestions}
            onSelectSuggestion={option => handleOnSubmit(option)}
            userName={userName}
          />
        )}
        {hasMessages &&
          currentMessages.map((group, groupIndex) => (
            <div key={`group-messages-${groupIndex}`}>
              <S.SectionMessagesLabel>
                <S.Divider />
                <span>{group?.label ?? '-'}</span>
              </S.SectionMessagesLabel>
              <S.Messages>
                {group?.messages.map((message, messageIndex) => (
                  <Message
                    key={`message-${groupIndex}-${messageIndex}`}
                    isFullScreen={isFullScreen}
                    userInitials={userInitials}
                    {...message}
                  />
                ))}
                {pendingSocketResponse && onLastGroup(groupIndex) && (
                  <Message
                    content="..."
                    isFullScreen={isFullScreen}
                    pendingSocketResponse
                    sender="server"
                    type="default"
                  />
                )}
              </S.Messages>
            </div>
          ))}
      </S.Content>
      {isLoading && <S.LinearProgress />}
      <S.Footer>
        <S.MessageInput
          variant="outlined"
          value={currentMessage}
          onKeyDown={handleKeyDown}
          onChange={({ target }) => setCurrentMessage(target.value)}
          placeholder="Digite sua mensagem para o Mobi Copiloto"
          disabled={generalLoading}
          inputRef={input => {
            // * Trigger focus after send message
            if (input !== null) input.focus();
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  aria-label="Enviar mensagem"
                  onClick={() => handleOnSubmit(currentMessage)}
                  onMouseDown={event => event.preventDefault()}
                  disabled={generalLoading}
                >
                  <SendIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </S.Footer>
    </S.Container>
  );
}
